/**
 * External link detection and preview utilities.
 * Supports multiple providers (Jira, Linear, Notion, etc.)
 */

import {AttachmentType} from "@/pb/api/devplan/types/attachments";
import type {ExternalLink, ExternalLinkPreview, IssueAttachmentMetadata} from "@/pb/api/devplan/types/attachments";

/**
 * Supported link providers.
 * Extend this type when adding new providers.
 */
export type LinkProvider = "jira" | "linear" | "linear-project" | "notion" | "google-drive" | "confluence";

/**
 * Configuration for a link provider's URL pattern.
 */
interface ProviderConfig {
  /** Regex pattern for matching URLs (identifier as capture group 1) */
  pattern: RegExp;
  /** Non-global version for single URL testing */
  singlePattern: RegExp;
  /** Function to normalize the URL */
  normalize: (url: string) => string;
  /** Optional provider-specific identifier extraction */
  extractIdentifier?: (url: string, match: RegExpMatchArray) => string | null;
}

const NOTION_ID_RE = /([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[?#/]|$)/i;

/**
 * Strips query parameters and hash fragments from a URL.
 */
function stripQueryAndHash(url: string): string {
  try {
    const questionIndex = url.indexOf('?');
    const hashIndex = url.indexOf('#');

    let endIndex = url.length;

    if (questionIndex !== -1 && hashIndex !== -1) {
      endIndex = Math.min(questionIndex, hashIndex);
    } else if (questionIndex !== -1) {
      endIndex = questionIndex;
    } else if (hashIndex !== -1) {
      endIndex = hashIndex;
    }

    return url.substring(0, endIndex);
  } catch {
    return url;
  }
}

function stripTrailingUrlPunctuation(url: string): string {
  return url.replace(/[),.;\]]+$/g, "");
}

function stripQueryHashAndTrailingPunctuation(url: string): string {
  return stripQueryAndHash(stripTrailingUrlPunctuation(url));
}

/**
 * Normalizes a Linear URL by stripping the title slug.
 */
function normalizeLinearUrl(url: string): string {
  const stripped = stripQueryAndHash(url);
  const match = stripped.match(/^(https?:\/\/linear\.app\/[^\/]+\/issue\/[A-Z][A-Z0-9]*-\d+)/);
  return match ? match[1] : stripped;
}

/**
 * Normalizes a Linear project URL by stripping query params and trailing segments.
 */
function normalizeLinearProjectUrl(url: string): string {
  const stripped = stripQueryAndHash(url);
  const match = stripped.match(/^(https?:\/\/linear\.app\/[^\/]+\/project\/[^\s\/]+)/);
  return match ? match[1] : stripped;
}

function normalizeNotionUrl(url: string): string {
  return stripQueryHashAndTrailingPunctuation(url);
}

function extractNotionIdentifier(url: string): string | null {
  return normalizeNotionUrl(url).match(NOTION_ID_RE)?.[1]?.replace(/-/g, "") ?? null;
}

function normalizeGoogleDriveUrl(url: string): string {
  const stripped = stripTrailingUrlPunctuation(url);
  try {
    const parsed = new URL(stripped);
    if (parsed.hostname === "drive.google.com") {
      const fileMatch = parsed.pathname.match(/^\/file\/d\/([^/]+)/);
      if (fileMatch?.[1]) return `https://drive.google.com/file/d/${fileMatch[1]}`;
      const folderMatch = parsed.pathname.match(/^\/drive\/folders\/([^/]+)/);
      if (folderMatch?.[1]) return `https://drive.google.com/drive/folders/${folderMatch[1]}`;
      const openId = parsed.pathname === "/open" ? parsed.searchParams.get("id") : null;
      if (openId) return `https://drive.google.com/file/d/${openId}`;
    }
    if (parsed.hostname === "docs.google.com") {
      const docMatch = parsed.pathname.match(/^\/(document|spreadsheets|presentation)\/d\/([^/]+)/);
      if (docMatch?.[1] && docMatch?.[2]) return `https://docs.google.com/${docMatch[1]}/d/${docMatch[2]}`;
    }
  } catch {
    return stripQueryHashAndTrailingPunctuation(url);
  }
  return stripQueryHashAndTrailingPunctuation(url);
}

function extractGoogleDriveIdentifier(url: string): string | null {
  const normalized = normalizeGoogleDriveUrl(url);
  const match = normalized.match(/^https?:\/\/(?:drive|docs)\.google\.com\/(?:file\/d\/|drive\/folders\/|(?:document|spreadsheets|presentation)\/d\/)([^/]+)/);
  return match?.[1] ?? null;
}

function normalizeConfluenceUrl(url: string): string {
  const stripped = stripTrailingUrlPunctuation(url);
  try {
    const parsed = new URL(stripped);
    if (parsed.pathname === "/wiki/pages/viewpage.action") {
      const pageId = parsed.searchParams.get("pageId");
      if (pageId) return `${parsed.origin}/wiki/pages/${pageId}`;
    }
    const editPageMatch = parsed.pathname.match(/\/wiki\/(?:spaces\/[^/]+\/)?pages\/edit-v\d+\/([^/]+)/);
    if (editPageMatch?.[1]) {
      return `${parsed.origin}/wiki/pages/${editPageMatch[1]}`;
    }
    const pageMatch = parsed.pathname.match(/\/wiki\/(?:spaces\/[^/]+\/)?pages\/(?!edit-v\d+\/)([^/]+)/);
    if (pageMatch?.[1]) {
      return `${parsed.origin}/wiki/pages/${pageMatch[1]}`;
    }
    const spaceMatch = parsed.pathname.match(/\/wiki\/spaces\/([^/]+)/);
    if (spaceMatch?.[1]) {
      return `${parsed.origin}/wiki/spaces/${spaceMatch[1]}`;
    }
  } catch {
    return stripQueryHashAndTrailingPunctuation(url);
  }
  return stripQueryHashAndTrailingPunctuation(url);
}

function extractConfluenceIdentifier(url: string): string | null {
  const normalized = normalizeConfluenceUrl(url);
  const pageMatch = normalized.match(/\/wiki\/pages\/([^/]+)/);
  if (pageMatch?.[1]) return pageMatch[1];
  const spaceMatch = normalized.match(/\/wiki\/spaces\/([^/]+)/);
  return spaceMatch?.[1] ?? null;
}

/**
 * Provider configurations registry.
 * Add new providers here.
 */
const PROVIDERS: Record<LinkProvider, ProviderConfig> = {
  jira: {
    pattern: /https?:\/\/[^\s\/]+\/browse\/([A-Z][A-Z0-9]*-\d+)/g,
    singlePattern: /^https?:\/\/[^\s\/]+\/browse\/([A-Z][A-Z0-9]*-\d+)/,
    normalize: stripQueryAndHash,
  },
  linear: {
    pattern: /https?:\/\/linear\.app\/[^\s\/]+\/issue\/([A-Z][A-Z0-9]*-\d+)/g,
    singlePattern: /^https?:\/\/linear\.app\/[^\s\/]+\/issue\/([A-Z][A-Z0-9]*-\d+)/,
    normalize: normalizeLinearUrl,
  },
  "linear-project": {
    pattern: /https?:\/\/linear\.app\/[^\s\/]+\/project\/([^\s\/]+)/g,
    singlePattern: /^https?:\/\/linear\.app\/[^\s\/]+\/project\/([^\s\/]+)/,
    normalize: normalizeLinearProjectUrl,
  },
  notion: {
    pattern: /https?:\/\/(?:(?:www\.)?(?:[^\s/]+\.)?(?:notion\.so|notion\.site)|app\.notion\.com)\/[^\s]+/g,
    singlePattern: /^https?:\/\/(?:(?:www\.)?(?:[^\s/]+\.)?(?:notion\.so|notion\.site)|app\.notion\.com)\/[^\s]+/,
    normalize: normalizeNotionUrl,
    extractIdentifier: (url) => extractNotionIdentifier(url),
  },
  "google-drive": {
    pattern: /https?:\/\/(?:drive|docs)\.google\.com\/(?:file\/d\/|drive\/folders\/|open\?id=|(?:document|spreadsheets|presentation)\/d\/)[^\s]+/g,
    singlePattern: /^https?:\/\/(?:drive|docs)\.google\.com\/(?:file\/d\/|drive\/folders\/|open\?id=|(?:document|spreadsheets|presentation)\/d\/)[^\s]+/,
    normalize: normalizeGoogleDriveUrl,
    extractIdentifier: (url) => extractGoogleDriveIdentifier(url),
  },
  confluence: {
    pattern: /https?:\/\/[^\s/]+\.atlassian\.net\/wiki\/(?:spaces\/[^\s/]+|(?:spaces\/[^\s/]+\/)?pages\/[^\s/]+)[^\s]*/g,
    singlePattern: /^https?:\/\/[^\s/]+\.atlassian\.net\/wiki\/(?:spaces\/[^\s/]+|(?:spaces\/[^\s/]+\/)?pages\/(?:viewpage\.action|[^\s/]+))[^\s]*/,
    normalize: normalizeConfluenceUrl,
    extractIdentifier: (url) => extractConfluenceIdentifier(url),
  },
};

/**
 * Checks if a URL matches a specific provider's pattern.
 */
export function isProviderUrl(url: string, provider: LinkProvider): boolean {
  const config = PROVIDERS[provider];
  const normalizedUrl = config.normalize(url);
  return config.singlePattern.test(normalizedUrl) && !!extractIdentifier(normalizedUrl, provider);
}

/**
 * Extracts the identifier from a URL for a specific provider.
 */
export function extractIdentifier(url: string, provider: LinkProvider): string | null {
  const config = PROVIDERS[provider];
  const normalizedUrl = config.normalize(url);
  const match = normalizedUrl.match(config.singlePattern);
  return match ? (config.extractIdentifier?.(normalizedUrl, match) ?? match[1] ?? null) : null;
}

/**
 * Extracts all links from text for a specific provider.
 */
export function extractProviderLinks(text: string, provider: LinkProvider): ExternalLink[] {
  const config = PROVIDERS[provider];
  const seen = new Set<string>();
  const results: ExternalLink[] = [];

  const regex = new RegExp(config.pattern.source, 'g');

  let match;
  while ((match = regex.exec(text)) !== null) {
    const rawUrl = match[0];
    const normalizedUrl = config.normalize(rawUrl);
    const identifier = config.extractIdentifier?.(normalizedUrl, match) ?? match[1];

    if (!identifier) {
      continue;
    }

    if (!seen.has(normalizedUrl)) {
      seen.add(normalizedUrl);
      results.push({
        url: normalizedUrl,
        provider,
        identifier,
      });
    }
  }

  return results;
}

/**
 * Extracts all external links from text across specified providers.
 * Results are deduplicated by normalized URL.
 */
export function getExternalLinkKey(link: ExternalLink): string {
  return `${link.provider}:${link.identifier}:${link.url}`;
}

export function extractAllLinks(text: string, providers?: LinkProvider[]): ExternalLink[] {
  const activeProviders = providers ?? (Object.keys(PROVIDERS) as LinkProvider[]);
  const seen = new Set<string>();
  const results: ExternalLink[] = [];

  for (const provider of activeProviders) {
    const links = extractProviderLinks(text, provider);
    for (const link of links) {
      if (!seen.has(link.url)) {
        seen.add(link.url);
        results.push(link);
      }
    }
  }

  return results;
}

/**
 * Normalizes a URL for a specific provider.
 */
export function normalizeUrl(url: string, provider: LinkProvider): string {
  return PROVIDERS[provider].normalize(url);
}

export function externalLinkToIssueAttachmentMetadata(link: ExternalLink): IssueAttachmentMetadata | undefined {
  if (!link.provider || !link.identifier) {
    return undefined;
  }

  const provider = link.provider.toLowerCase();
  const type = provider === "jira"
    ? AttachmentType.JIRA_ISSUE
    : provider === "linear"
      ? AttachmentType.LINEAR_ISSUE
      : provider === "linear-project"
        ? AttachmentType.LINEAR_PROJECT
        : provider === "notion" || provider === "google-drive" || provider === "confluence"
          ? AttachmentType.URL
          : AttachmentType.UNKNOWN_ATTACHMENT_TYPE;
  if (type === AttachmentType.UNKNOWN_ATTACHMENT_TYPE) {
    return undefined;
  }

  return {
    type,
    issueId: link.identifier,
    humanReadableId: link.identifier,
    scopeId: "",
    url: link.url || "",
  };
}

export function issueAttachmentMetadataForExternalLinks(
  links: ExternalLink[],
  metadataByKey: Record<string, IssueAttachmentMetadata> = {},
): IssueAttachmentMetadata[] {
  const out: IssueAttachmentMetadata[] = [];
  for (const link of links) {
    const key = getExternalLinkKey(link);
    const metadata = metadataByKey[key] ?? externalLinkToIssueAttachmentMetadata(link);
    if (metadata) {
      out.push(metadata);
    }
  }
  return out;
}

// ============================================
// Convenience functions for specific providers
// ============================================

export function isJiraUrl(url: string): boolean {
  return isProviderUrl(url, "jira");
}

export function isLinearUrl(url: string): boolean {
  return isProviderUrl(url, "linear");
}

export function extractJiraIssueKey(url: string): string | null {
  return extractIdentifier(url, "jira");
}

export function extractLinearIssueKey(url: string): string | null {
  return extractIdentifier(url, "linear");
}

export function extractAllJiraUrls(text: string): ExternalLink[] {
  return extractProviderLinks(text, "jira");
}

export function extractAllLinearUrls(text: string): ExternalLink[] {
  return extractProviderLinks(text, "linear");
}

export function normalizeJiraUrl(url: string): string {
  return normalizeUrl(url, "jira");
}

export function isLinearProjectUrl(url: string): boolean {
  return isProviderUrl(url, "linear-project");
}

export function extractLinearProjectSlug(url: string): string | null {
  return extractIdentifier(url, "linear-project");
}

export function extractAllLinearProjectUrls(text: string): ExternalLink[] {
  return extractProviderLinks(text, "linear-project");
}

/**
 * Convert preview payload into structured issue attachment metadata for UserMessage.issueAttachments.
 * Uses preview.issue when available and falls back to normalized link metadata.
 */
export function previewToIssueAttachmentMetadata(preview: ExternalLinkPreview): IssueAttachmentMetadata | undefined {
  if (preview.issue) {
    return {
      ...preview.issue,
      humanReadableId: preview.issue.humanReadableId || preview.issue.issueId || "",
    };
  }

  return preview.link ? externalLinkToIssueAttachmentMetadata(preview.link) : undefined;
}
